import { computed, ref, type ComputedRef, type Ref } from "vue";
import {
  ANSWER_TYPES,
  SKIPPED_VERSION_TYPE,
  type EditableAssessment,
  type RiskAssessmentAnswer,
  type RiskAssessmentNewResponse,
  type RiskAssessmentVersion
} from "@/vue/entities/risk-assessment";
import type { AnswerDraft, RiskAssessmentFormMode } from "./types";

// 相談を離れる際に区分のストアへ預ける形（riskAssessmentDraftStore.ts）。
export type RiskAssessmentDraftState = {
  newResponse: RiskAssessmentNewResponse;
  draftByVersion: Record<number, AnswerDraft[]>;
  selectedVersionType: number | null;
  mode: RiskAssessmentFormMode;
};

// 表示中の相談の入力内容を保持する（FR-012 / DR-002）。
// localStorage / sessionStorage / Cookie には一切保存せず、要配慮個人情報を端末に残さない。
// モジュールスコープの変数も使わないため、別カルテへ内容が漏れることが構造的に起こらない。
export type RiskAssessmentDraft = {
  newResponse: Ref<RiskAssessmentNewResponse | null>;
  draftByVersion: Ref<Record<number, AnswerDraft[]>>;
  selectedVersionType: Ref<number | null>;
  mode: Ref<RiskAssessmentFormMode>;
  editableAssessment: ComputedRef<EditableAssessment | null>;
  isRetained: ComputedRef<boolean>;
  initialize: (response: RiskAssessmentNewResponse) => void;
  clear: () => void;
  snapshot: () => RiskAssessmentDraftState | null;
  restore: (state: RiskAssessmentDraftState) => void;
};

// 修正モードでは修正対象の回答を、それ以外は当該Verの前回入力を初期選択値にする（FR-006-1 / AC-005）。
const initialAnswersFor = (response: RiskAssessmentNewResponse, versionType: number): RiskAssessmentAnswer[] => {
  const editable = response.editableAssessment;
  if (editable && editable.versionType === versionType) return editable.answers;

  return response.previousAssessments?.[String(versionType)]?.answers ?? [];
};

// 初期値は配列ごとコピーして独立させる。今回の選択変更が「前回入力」列へ波及しない（REQ-006-4 / AC-006）。
const buildDrafts = (version: RiskAssessmentVersion, answers: RiskAssessmentAnswer[]): AnswerDraft[] => {
  const answerMap = new Map(answers.map((answer) => [answer.questionNo, answer]));

  return version.questions.map((question) => {
    const answer = answerMap.get(question.questionNo);

    return {
      questionNo: question.questionNo,
      // 保存済みの 0（未回答）は画面上は未選択として扱う。
      answerType: answer?.answerType === ANSWER_TYPES.unanswered ? null : answer?.answerType ?? null,
      detailKeys: [...(answer?.detailKeys ?? [])]
    };
  });
};

// 修正対象がスキップ（versionType: null）の場合は擬似値へ寄せ、「未選択」と区別できるようにする。
const initialVersionTypeOf = (response: RiskAssessmentNewResponse): number | null => {
  const editable = response.editableAssessment;
  if (!editable) return response.defaultVersionType;

  return editable.versionType ?? SKIPPED_VERSION_TYPE;
};

export const useRiskAssessmentDraft = (): RiskAssessmentDraft => {
  const newResponse = ref<RiskAssessmentNewResponse | null>(null);
  const draftByVersion = ref<Record<number, AnswerDraft[]>>({});
  const selectedVersionType = ref<number | null>(null);
  const mode = ref<RiskAssessmentFormMode>("create");

  const editableAssessment = computed<EditableAssessment | null>(() => newResponse.value?.editableAssessment ?? null);

  const isRetained = computed<boolean>(() => newResponse.value !== null);

  const initialize = (response: RiskAssessmentNewResponse): void => {
    newResponse.value = response;
    mode.value = response.editableAssessment ? "edit" : "create";
    selectedVersionType.value = initialVersionTypeOf(response);
    // Ver切替で入力が消えないよう、2Ver分の回答をあらかじめ保持する（ui-design.md 4.2）。
    draftByVersion.value = response.versions.reduce<Record<number, AnswerDraft[]>>((drafts, version) => {
      drafts[version.versionType] = buildDrafts(version, initialAnswersFor(response, version.versionType));
      return drafts;
    }, {});
  };

  const clear = (): void => {
    newResponse.value = null;
    draftByVersion.value = {};
    selectedVersionType.value = null;
    mode.value = "create";
  };

  const snapshot = (): RiskAssessmentDraftState | null => {
    if (newResponse.value === null) return null;

    return {
      newResponse: newResponse.value,
      draftByVersion: draftByVersion.value,
      selectedVersionType: selectedVersionType.value,
      mode: mode.value
    };
  };

  const restore = (state: RiskAssessmentDraftState): void => {
    newResponse.value = state.newResponse;
    draftByVersion.value = state.draftByVersion;
    selectedVersionType.value = state.selectedVersionType;
    mode.value = state.mode;
  };

  return {
    newResponse,
    draftByVersion,
    selectedVersionType,
    mode,
    editableAssessment,
    isRetained,
    initialize,
    clear,
    snapshot,
    restore
  };
};
